import { useCallback, useEffect, useState } from 'react';
import { Header, Section } from '../../shared/components';
import { getOperationTimesRepository } from '../../shared/services/operationTimes/repositoryFactory';
import { getSettingsRepository } from '../../shared/services/settings/repositoryFactory';
import { OperationTimeRecord } from '../../shared/services/operationTimes/types';
import { ParamActivity, ParamUser } from '../../shared/services/settings/types';
import { ConsolidadoFilters, ConsolidadoFiltersState } from './components/ConsolidadoFilters';
import { ConsolidadoTable } from './components/ConsolidadoTable';
import { ConsolidadoExportButtons } from './components/ConsolidadoExportButtons';

const operationTimesRepo = getOperationTimesRepository();
const settingsRepo = getSettingsRepository();

function todayInputValue(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Modulo: Consolidado. Historial GLOBAL de registros de Tiempos (todos los
 * usuarios) con todos los campos - incluida "Modificado / Borrado" - mas
 * herramientas de descarga (Excel, CSV, Excel con tabla dinamica).
 *
 * Exclusivo de Administrador (ver RequireAdmin en el router y
 * ADMIN_ONLY_MODULE_IDS en shared/config/modules.ts).
 *
 * Es una pantalla de SOLO CONSULTA Y DESCARGA - no edita ni elimina registros
 * (para eso ya existen Control de Tiempos y Auditoría). Usa el mismo
 * repositorio de datos que esas pantallas (misma fuente de verdad), pero con
 * componentes de filtros/tabla/exportacion propios y 100% independientes -
 * ningun cambio aqui afecta a Tiempos, Reportes ni Auditoría.
 */
export function ConsolidadoPage() {
  const [activities, setActivities] = useState<ParamActivity[]>([]);
  const [users, setUsers] = useState<ParamUser[]>([]);
  const [records, setRecords] = useState<OperationTimeRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState<ConsolidadoFiltersState>({
    userId: '',
    activityId: '',
    dateFrom: todayInputValue(),
    dateTo: todayInputValue(),
    novedad: '',
  });

  const loadRecords = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await operationTimesRepo.list({
        userId: filters.userId || undefined,
        activityId: filters.activityId || undefined,
        // Mismo criterio de zona horaria que Control de Tiempos: se resuelve
        // la fecha local del navegador antes de convertir a UTC ("Z"), para
        // que el rango de dias no se desplace por el offset horario.
        dateFrom: filters.dateFrom ? new Date(`${filters.dateFrom}T00:00:00`).toISOString() : undefined,
        dateTo: filters.dateTo ? new Date(`${filters.dateTo}T23:59:59.999`).toISOString() : undefined,
        novedad: filters.novedad || undefined,
        page: 1,
        pageSize: 1000,
      });
      setRecords(result.items);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    // includeInactive=true: un usuario o actividad desactivado despues de
    // crear el registro debe poder seguir viendose/filtrandose aqui.
    settingsRepo.listActivities(true).then(setActivities);
    settingsRepo.listUsers(true).then(setUsers);
  }, []);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  return (
    <div>
      <Header title="Consolidado" subtitle="Historial global de registros de todos los usuarios, con descarga en Excel y CSV" />
      <div className="space-y-6 p-4">
        <Section title="Filtros">
          <ConsolidadoFilters value={filters} onChange={setFilters} users={users} activities={activities} />
        </Section>

        <Section title="Descargar">
          <ConsolidadoExportButtons records={records} disabled={isLoading} />
        </Section>

        <Section title="Historial de registros">
          <ConsolidadoTable rows={records} isLoading={isLoading} />
        </Section>
      </div>
    </div>
  );
}
